import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Award, X, Flame, Share2 } from "lucide-react";
import { shareBadgeImage } from "../lib/shareBadge";

const CONF_COLORS = ["#C41E24", "#F5C518", "#ffffff", "#C9CBD1", "#22C55E"];

const badgeMeta = (b) =>
  b >= 100
    ? { color: "#F5C518", label: "100-Day", tier: "Century Streak", blurb: "One hundred days of showing up. You're in elite company now." }
    : b >= 30
    ? { color: "#C9CBD1", label: "30-Day", tier: "Monthly Streak", blurb: "A full month of daily work. This is how elite players are built." }
    : { color: "#CD7F32", label: "7-Day", tier: "Weekly Streak", blurb: "Seven days straight. The habit is forming — keep the fire going!" };

export default function BadgeCelebration({ badge, onClose }) {
  const meta = badgeMeta(badge);
  const pieces = useMemo(
    () =>
      Array.from({ length: 60 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.6,
        duration: 2.2 + Math.random() * 1.6,
        drift: (Math.random() - 0.5) * 160,
        color: CONF_COLORS[i % CONF_COLORS.length],
        size: 6 + Math.random() * 8,
        rot: Math.random() * 360,
      })),
    []
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="badge-celebration">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      {/* confetti */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {pieces.map((p) => (
          <motion.div
            key={p.id}
            initial={{ y: -40, x: 0, opacity: 0, rotate: 0 }}
            animate={{ y: 900, x: p.drift, opacity: [0, 1, 1, 0.8], rotate: p.rot + 540 }}
            transition={{ duration: p.duration, delay: p.delay, ease: "easeIn", repeat: Infinity, repeatDelay: 0.4 }}
            style={{ position: "absolute", top: 0, left: `${p.left}%`, width: p.size, height: p.size * 1.4, backgroundColor: p.color, borderRadius: 2 }}
          />
        ))}
      </div>

      <motion.div
        initial={{ scale: 0.7, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 18 }}
        className="relative bg-[#141414] border border-white/10 rounded-2xl p-8 max-w-sm w-full text-center shadow-[0_24px_80px_rgba(0,0,0,0.7)]"
      >
        <button data-testid="badge-celebration-close" onClick={onClose} className="absolute top-3 right-3 text-white/40 hover:text-white transition-colors"><X size={20} /></button>

        <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#C41E24] mb-5">Badge Unlocked!</div>

        <motion.div
          initial={{ scale: 0, rotate: -30 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 12 }}
          className="mx-auto w-28 h-28 rounded-full flex items-center justify-center mb-5 relative"
          style={{ backgroundColor: `${meta.color}1f`, border: `2px solid ${meta.color}` }}
        >
          <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1.6, repeat: Infinity }} className="absolute inset-0 rounded-full" style={{ boxShadow: `0 0 40px ${meta.color}66` }} />
          <Award size={54} style={{ color: meta.color }} />
        </motion.div>

        <h3 className="font-cover text-5xl uppercase leading-none" style={{ color: meta.color }}>{meta.label}</h3>
        <div className="flex items-center justify-center gap-1.5 mt-2 mb-4">
          <Flame size={16} className="text-[#C41E24]" />
          <span className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/80">{meta.tier}</span>
        </div>
        <p className="text-sm text-white/60 mb-6">{meta.blurb}</p>

        <button data-testid="badge-celebration-cta" onClick={onClose} className="w-full bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider py-3 rounded-sm transition-colors">
          Keep The Streak Alive
        </button>
        <button data-testid="badge-share" onClick={() => shareBadgeImage(badge)} className="w-full mt-2 flex items-center justify-center gap-2 bg-transparent border border-white/20 hover:border-white text-white font-subheadings font-bold uppercase tracking-wider py-3 rounded-sm transition-colors">
          <Share2 size={16} className="text-[#C41E24]" /> Share My Badge
        </button>
      </motion.div>
    </div>
  );
}
